import {
  ContractType,
  ContractTypeDisplay,
  EpcCompanyType,
  ExpensesUnit,
  PricingType,
  ProjectCustomerPricing,
  ProjectInterconnectionDisplayValues,
  ProjectOfftakeContract,
  ProjectPermits,
  PropertyTaxType,
  PropertyTaxTypeDisplayValues,
  RecAllocationDisplay,
  RecOwnership,
  RecOwnershipDisplay,
  RecProgramYearDisplay,
  RecProjectCategoryDisplay,
  RecShinesProgram,
  RecStates,
  RecUtilityGroupExcelDisplay,
  RevenueSource,
  RevenueUnit,
  SiteControlStatusDisplay,
  SiteLeaseTermDisplayExcel,
  SystemOwner,
  TaxUnit,
  Unit,
} from '~/constants';
import { DevelopmentStages, DevelopmentStageDisplay } from '~/constants/development-stage';
import { DeploymentTypeDisplay } from '~/constants/deployment-type';
import { ITCAdderCodes } from '~/constants/itc-adder-codes';
import { ProjectArrays, ProjectArraysDisplay } from '~/constants/project-arrays';
import { Project } from '~/types/project';
import calculateProjectArrayTotals from './calculateProjectArrayTotals';
import { getFees, getStorageFees } from '~/utils/calculators/fees';
import { getExpectedItc, getExpectedItcCost } from '~/utils/calculators/costsFns';

export type CellValue = string | number | boolean | null | undefined;

export type ManifestFormat =
  | 'currency'
  | 'currencyPerW'
  | 'integer'
  | 'decimal2'
  | 'decimal4'
  | 'percent'
  | 'percentInteger'
  | 'percentRaw'
  | 'date'
  | 'text'
  | 'yesNo';

export interface ExtractContext {
  projectCosts?: any;
  projectDisplayValues?: any;
}

export interface ManifestEntry {
  /** Worksheet section id this row belongs to (`overview`, `system`, `budget`, `revenue`, `expenses`) */
  sectionId: string;
  /** Worksheet subsection id (matches `ProjectWorksheetConfig`) */
  subsectionId: string;
  /** Stable id for this row in the portfolio sheet (used in tests/snapshots). */
  id: string;
  /** Field id from the worksheet schema; conditions evaluator queries this id. Omit for synthetic rows that have no schema field — gating then falls back to section/subsection. */
  fieldId?: string;
  /** Override condition gating to a different worksheet field id. Used when one worksheet field controls multiple synthetic rows (e.g. budgetCosts → 3 rows). */
  gateFieldId?: string;
  /** Default label when the field has no conditional override or no `fieldId`. */
  label: string;
  /** Optional override: compute the label from the set of projects shown (e.g. relabel a row when every visible project has a particular bid type). Return `undefined` (or omit) to fall back to the worksheet-mirrored / default label. */
  labelFor?: (projects: Project[]) => string | undefined;
  /** Optional override: compute the number format from the set of visible projects (e.g. show the user's original unit without converting). Falls back to `format` when omitted. */
  formatFor?: (projects: Project[]) => ManifestFormat | undefined;
  /** When the extracted value is empty *and* this predicate matches the project, render the literal `TO BID` (in black) instead of a blank cell — for bid rows whose value hasn't been provided yet. */
  bidPlaceholder?: (project: Project) => boolean;
  /** When the worksheet supplies a conditional `fieldLabel` (which is unit-less), append this suffix to form the spreadsheet label. The default `label` should already include any unit suffix. */
  unitSuffix?: string;
  format?: ManifestFormat;
  /** Whether the Totals column should sum this row across projects. Only numeric formats. */
  totalEligible?: boolean;
  /** Hide this row entirely when every visible project produces an empty value. */
  hideWhenAllEmpty?: boolean;
  /** Pull a cell value for one project. Return `undefined`/`null`/`''` for blank. */
  extract: (project: Project, ctx: ExtractContext) => CellValue;
}

const arrayTotals = (project: Project) => {
  try {
    return calculateProjectArrayTotals(project as any).projectArrayAmounts.TOTAL;
  } catch {
    return { sizeKwdc: undefined, sizeKwac: undefined, production: undefined, productionFactor: undefined };
  }
};

const longTermOwnerDisplay = (project: Project, ctx: ExtractContext): CellValue => {
  const isITC = project.pricingType === PricingType.ITC;
  if (isITC && project.systemOwner === SystemOwner.CUSTOMER) return project.customerName;
  if (isITC && project.systemOwner === SystemOwner.THIRD_PARTY) return project.systemOwnerName;
  return project.investor?.company?.name ?? ctx.projectDisplayValues?.projectDetails?.longTermOwner;
};

const epcCompanyDisplay = (project: Project): CellValue => {
  switch (project.epcCompanyType) {
    case EpcCompanyType.SELF: return project.installer?.company?.name ?? 'Self';
    case EpcCompanyType.OTHER_DEVELOPER: return project.epcCompanyName;
    case EpcCompanyType.OTHER_INVESTOR: return 'Investor to Choose';
    default: return undefined;
  }
};

const propertyTaxPerYear = (project: Project): number | undefined => {
  const amount = Number(project.propertyTaxAmount);
  if (!Number.isFinite(amount) || amount === 0) return undefined;
  if (project.propertyTaxType === PropertyTaxType.ANNUAL) return amount;
  if (project.propertyTaxType !== PropertyTaxType.PILOT) return undefined;
  const unit = typeof project.propertyTaxUnit === 'string' ? project.propertyTaxUnit.toUpperCase() : project.propertyTaxUnit;
  if (unit === TaxUnit.YEAR) return amount;
  const totals = arrayTotals(project);
  if (unit === TaxUnit.KWDC && totals.sizeKwdc) return totals.sizeKwdc * amount;
  if (unit === TaxUnit.KWAC && totals.sizeKwac) return totals.sizeKwac * amount;
  return undefined;
};

const ITC_SAFE_HARBOR_STRATEGY_LABELS: Record<string, string> = {
  spend_test_small: 'Met the 5% spend test (Project <1.5MWac)',
  spend_test_large: 'Met the 5% spend test before 9/2/25 (Project >1.5MWac)',
  construction_onsite: 'Construction started via onsite physical work',
  construction_offsite: 'Construction started via offsite physical work',
  na_bess_only: 'N/A - BESS project only',
};

const FEOC_SAFE_HARBOR_STRATEGY_LABELS: Record<string, string> = {
  spend_test_before_2026: 'Met the 5% spend test before 1/1/26',
  construction_onsite_before_2026: 'Construction started via onsite physical work before 1/1/26',
  construction_offsite_before_2026: 'Construction started via offsite physical work before 1/1/26',
};

/** Parse Postgres TEXT[] array literal syntax: `{val,"val with space",val3}`. */
const parsePgArrayLiteral = (s: string): string[] => {
  const inner = s.slice(1, -1);
  if (inner.length === 0) return [];
  const out: string[] = [];
  let buf = '';
  let inQuote = false;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (inQuote) {
      if (ch === '\\' && i + 1 < inner.length) {
        buf += inner[++i];
      } else if (ch === '"') {
        inQuote = false;
      } else {
        buf += ch;
      }
    } else if (ch === '"') {
      inQuote = true;
    } else if (ch === ',') {
      out.push(buf);
      buf = '';
    } else {
      buf += ch;
    }
  }
  out.push(buf);
  return out.map(v => v.trim()).filter(v => v.length > 0 && v !== 'NULL');
};

const formatStrategyList = (value: unknown, labels: Record<string, string>): string | undefined => {
  if (value == null || value === '') return undefined;
  // Tolerate any of: string code, array of code strings, array of option
  // objects ({ value }/{ code }/{ key }), a JSON-encoded form, or a Postgres
  // TEXT[] array literal like `{"spend_test_small","construction_onsite"}`.
  let raw: unknown = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      // Could be a Postgres array literal OR a JSON object. Postgres arrays
      // either contain `"`-quoted items or comma-separated bare codes; JSON
      // objects start with `{"key":`. Try PG-array parse first.
      const pgParsed = parsePgArrayLiteral(trimmed);
      if (pgParsed.length > 0) {
        raw = pgParsed;
      } else {
        try { raw = JSON.parse(trimmed); } catch { /* fall through */ }
      }
    } else if (trimmed.startsWith('[') || trimmed.startsWith('"')) {
      try { raw = JSON.parse(trimmed); } catch { /* fall through */ }
    }
  }
  const arr = Array.isArray(raw) ? raw : [raw];
  const codes = arr
    .map((v: any) => {
      if (v == null) return undefined;
      if (typeof v === 'string' || typeof v === 'number') return String(v);
      if (typeof v === 'object') return String(v.value ?? v.code ?? v.key ?? '');
      return undefined;
    })
    .filter((c): c is string => typeof c === 'string' && c.length > 0);
  if (codes.length === 0) return undefined;
  const labeled = codes.map(c => labels[c] ?? c);
  return labeled.join('; ');
};

/** Adders selected on a project, in a stable canonical order matching ITCAdderCodes declaration. */
const ITC_ADDER_ORDER = [
  ITCAdderCodes.DOMESTIC_CONTENT,
  ITCAdderCodes.ENERGY_COMMUNITY,
  ITCAdderCodes.LOW_INCOME_COMMUNITY_TRIBAL,
  ITCAdderCodes.LOW_INCOME_ECONOMIC_BENEFIT,
];

const ITC_ADDER_LABELS: Record<string, string> = {
  [ITCAdderCodes.DOMESTIC_CONTENT]: 'Domestic Content (10%)',
  [ITCAdderCodes.ENERGY_COMMUNITY]: 'Energy Community (10%)',
  [ITCAdderCodes.LOW_INCOME_COMMUNITY_TRIBAL]: 'LI Community / Tribal (10%)',
  [ITCAdderCodes.LOW_INCOME_ECONOMIC_BENEFIT]: 'LI Economic Benefit (20%)',
};

export const orderedAdderLabelsFor = (project: Project): string[] => {
  const byCode = new Map<string, any>();
  for (const a of project.itcAdders ?? []) {
    const code = (a as any).code;
    if (code) byCode.set(code, a);
  }
  return ITC_ADDER_ORDER
    .filter(c => byCode.has(c))
    .map(c => {
      const adder = byCode.get(c);
      const label = adder?.label;
      const value = adder?.value;
      if (typeof label === 'string' && label.length > 0) {
        return typeof value === 'number' ? `${label} (${value}%)` : label;
      }
      return ITC_ADDER_LABELS[c];
    });
};

const siteLeasePerYear = (project: Project): number | undefined => {
  if (project.siteLeaseCost == null) return undefined;
  if (project.siteLeaseCostUnit === ExpensesUnit.KWDC) {
    const totals = arrayTotals(project);
    if (!totals.sizeKwdc) return undefined;
    return project.siteLeaseCost * totals.sizeKwdc;
  }
  return project.siteLeaseCost;
};

const omPerYear = (project: Project): number | undefined => {
  if (!project.omProposeFlag || project.omPrice == null) return undefined;
  if (project.omPriceUnit === ExpensesUnit.KWDC) {
    const totals = arrayTotals(project);
    if (!totals.sizeKwdc) return undefined;
    return project.omPrice * totals.sizeKwdc;
  }
  return project.omPrice;
};

/** State-specific REC fields are only surfaced when the investor owns the RECs. */
const recsDataFor = (project: Project): NonNullable<Project['recsData']>[string] | undefined => {
  if (project.recOwnership !== RecOwnership.INVESTOR) return undefined;
  if (!project.addressState) return undefined;
  return project.recsData?.[project.addressState];
};

const isIllinoisShines = (project: Project): boolean =>
  project.addressState === RecStates.ILLINOIS && recsDataFor(project)?.isShinesProgram === RecShinesProgram.YES;

const isNewJersey = (project: Project): boolean =>
  project.addressState === RecStates.NEW_JERSEY && recsDataFor(project) != null;

/** The upfront incentive value exactly as entered by the user — no unit conversion. */
const upfrontIncentiveRaw = (project: Project): number | undefined => {
  if (project.upfrontIncentives == null || project.upfrontIncentives === ('' as any)) return undefined;
  const amount = Number(project.upfrontIncentives);
  return Number.isFinite(amount) ? amount : undefined;
};

const upfrontIncentiveIsPerWdc = (project: Project): boolean => {
  const unit = typeof project.upfrontIncentiveUnit === 'string'
    ? project.upfrontIncentiveUnit.toUpperCase()
    : project.upfrontIncentiveUnit;
  return unit === Unit.WDC;
};

/** Pick the unit suffix shared by the projects that actually entered an upfront incentive. */
const upfrontIncentiveUnitSuffix = (projects: Project[]): '($)' | '($/Wdc)' => {
  const withValue = projects.filter(p => p.upfrontIncentives != null && p.upfrontIncentives !== ('' as any));
  return withValue.length > 0 && withValue.every(upfrontIncentiveIsPerWdc) ? '($/Wdc)' : '($)';
};

const epcCostTotal = (project: Project, ctx: ExtractContext): number | undefined => {
  const fromCosts = ctx.projectCosts?.epcCost;
  if (fromCosts != null) return fromCosts;
  if (project.epcCost == null) return undefined;
  if (project.epcCostUnit === Unit.TOTAL) return project.epcCost;
  const totals = arrayTotals(project);
  if (!totals.sizeKwdc) return undefined;
  return project.epcCost * totals.sizeKwdc * 1000;
};

const epcCostPerWdc = (project: Project, ctx: ExtractContext): number | undefined => {
  const totals = arrayTotals(project);
  const total$ = epcCostTotal(project, ctx);
  if (total$ == null || !totals.sizeKwdc) return undefined;
  return total$ / totals.sizeKwdc / 1000;
};

/** Relabel the EPC Cost budget rows to reflect what is actually being bid:
 * dev-fee bids quote a development fee, acquisition bids quote EPC cost + dev
 * fee (mirrors the "Bid Type Requested" convention). Returns undefined for any
 * other / mixed pricing so the row keeps its plain "EPC Cost" label. */
const epcBudgetLabelFor = (projects: Project[], unitSuffix: string): string | undefined => {
  if (projects.length === 0) return undefined;
  if (projects.every(p => p.pricingType === PricingType.DEV_FEE)) return `Development Fee ${unitSuffix}`;
  if (projects.every(p => p.pricingType === PricingType.ACQUISITION)) return `EPC Cost + Dev Fee ${unitSuffix}`;
  return undefined;
};

/** Lease and ESA offtake rates are quoted as an annual $ amount rather than $/kWh. */
const isLeaseOrEsaRate = (projects: Project[]): boolean =>
  projects.length > 0 && projects.every(p =>
    p.contractType === ContractType.LEASE || p.contractType === ContractType.ENERGY_SERVICE_AGREEMENT);

type BudgetCostKey = 'INTERCONNECTION' | 'OTHER_ITC' | 'OTHER_NON_ITC';

const budgetCostTotal = (project: Project, ctx: ExtractContext, key: BudgetCostKey): number | undefined => {
  const ctxKey = key === 'INTERCONNECTION' ? 'interconnection' : key === 'OTHER_ITC' ? 'otherItc' : 'otherNonItc';
  const fromCosts = (ctx.projectCosts as any)?.[ctxKey];
  if (fromCosts != null) return fromCosts;
  const item = project.budgetCosts?.[key];
  if (item?.amount == null) return undefined;
  if (item.unit === Unit.TOTAL) return item.amount;
  const totals = arrayTotals(project);
  if (!totals.sizeKwdc) return undefined;
  return item.amount * totals.sizeKwdc * 1000;
};

const budgetCostPerWdc = (project: Project, ctx: ExtractContext, key: BudgetCostKey): number | undefined => {
  const totals = arrayTotals(project);
  const total$ = budgetCostTotal(project, ctx, key);
  if (total$ == null || !totals.sizeKwdc) return undefined;
  return total$ / totals.sizeKwdc / 1000;
};

const conductorFeeTotal = (project: Project, ctx: ExtractContext): number | undefined => {
  if (project.pricingType === PricingType.ITC) {
    const expectedItcCost = ctx.projectCosts?.expectedItcCost ?? getExpectedItcCost(project, epcCostTotal(project, ctx));
    const nrsFeeRaw = (project as any).activeInvestorQueueRow?.nrsFee ?? (project as any).investorQueue?.[0]?.nrsFee;
    if (nrsFeeRaw == null || expectedItcCost == null) return undefined;
    return expectedItcCost * nrsFeeRaw / 100;
  }
  const epc = epcCostTotal(project, ctx);
  if (epc == null) return undefined;
  const pvFee = getFees(project, epc, { bid: ctx.projectCosts?.bid, hasNrsFee: true });
  const storageFee = project.hasStorage
    ? getStorageFees(project, { bid: ctx.projectCosts?.bid, hasBuyerStorageFee: true })
    : null;
  const pv = typeof pvFee === 'number' && !Number.isNaN(pvFee) ? pvFee : 0;
  const bess = typeof storageFee === 'number' && !Number.isNaN(storageFee) ? storageFee : 0;
  const sum = pv + bess;
  return sum > 0 ? sum : undefined;
};

const conductorFeePerWdc = (project: Project, ctx: ExtractContext): number | undefined => {
  const totals = arrayTotals(project);
  const total$ = conductorFeeTotal(project, ctx);
  if (total$ == null || !totals.sizeKwdc) return undefined;
  return total$ / totals.sizeKwdc / 1000;
};

const budgetGrandTotal = (project: Project, ctx: ExtractContext): number | undefined => {
  const parts = [
    epcCostTotal(project, ctx),
    budgetCostTotal(project, ctx, 'INTERCONNECTION'),
    budgetCostTotal(project, ctx, 'OTHER_ITC'),
    budgetCostTotal(project, ctx, 'OTHER_NON_ITC'),
    conductorFeeTotal(project, ctx),
  ];
  let sum = 0;
  let any = false;
  for (const v of parts) {
    if (typeof v === 'number' && !Number.isNaN(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? sum : undefined;
};

const budgetGrandTotalPerWdc = (project: Project, ctx: ExtractContext): number | undefined => {
  const totals = arrayTotals(project);
  const total$ = budgetGrandTotal(project, ctx);
  if (total$ == null || !totals.sizeKwdc) return undefined;
  return total$ / totals.sizeKwdc / 1000;
};

export const dealSpreadsheetManifest: ManifestEntry[] = [
  // Overview / Project foundations
  {
    sectionId: 'overview', subsectionId: 'projectFoundations', id: 'projectName',
    label: 'Project Name', format: 'text',
    extract: p => p.name,
  },
  {
    sectionId: 'overview', subsectionId: 'projectFoundations', id: 'developmentStage',
    fieldId: 'developmentStage', label: 'Development Stage', format: 'text',
    extract: p => p.developmentStage ? DevelopmentStageDisplay[p.developmentStage] : undefined,
  },
  {
    sectionId: 'overview', subsectionId: 'projectFoundations', id: 'deploymentType',
    fieldId: 'deploymentType', label: 'Deployment Type', format: 'text',
    extract: p => p.deploymentType ? DeploymentTypeDisplay[p.deploymentType] : undefined,
  },

  // Overview / Project roles
  {
    sectionId: 'overview', subsectionId: 'projectRoles', id: 'longTermOwner',
    fieldId: 'longTermOwner', label: 'Long-term System Owner', format: 'text',
    extract: longTermOwnerDisplay,
  },
  {
    sectionId: 'overview', subsectionId: 'projectRoles', id: 'pricingType',
    fieldId: 'pricingType', label: 'Bid Type Requested', format: 'text',
    extract: p => {
      switch (p.pricingType) {
        case PricingType.ACQUISITION: return p.developmentStage === DevelopmentStages.OPERATING ? 'Acquisition Price' : 'EPC Cost + Dev Fee';
        case PricingType.DEV_FEE: return 'Development Fee';
        case PricingType.OFFTAKE: return 'Offtake Rate';
        case PricingType.ITC: return 'ITC Transfer';
        default: return undefined;
      }
    },
  },
  {
    sectionId: 'overview', subsectionId: 'projectRoles', id: 'epcCompanyType',
    fieldId: 'epcCompanyType', label: 'EPC Company', format: 'text',
    extract: epcCompanyDisplay,
  },

  // Overview / Location
  {
    sectionId: 'overview', subsectionId: 'location', id: 'addressStreet',
    fieldId: 'addressStreet', label: 'Address', format: 'text',
    extract: p => p.addressStreet,
  },
  {
    sectionId: 'overview', subsectionId: 'location', id: 'addressCity',
    fieldId: 'addressCity', label: 'City', format: 'text',
    extract: p => p.addressCity,
  },
  {
    sectionId: 'overview', subsectionId: 'location', id: 'addressState',
    fieldId: 'addressState', label: 'State', format: 'text',
    extract: p => p.addressState,
  },
  {
    sectionId: 'overview', subsectionId: 'location', id: 'addressZip',
    fieldId: 'addressZip', label: 'ZIP code', format: 'text',
    extract: p => p.addressZip,
  },
  {
    sectionId: 'overview', subsectionId: 'location', id: 'utilityZone',
    fieldId: 'utilityZone', label: 'Utility Zone', format: 'text',
    extract: p => p.utilityZone,
  },

  // Overview / Key dates and statuses
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'expectedProceedDate',
    fieldId: 'expectedProceedDate', label: 'Expected NTP', format: 'date',
    extract: p => p.expectedProceedDate,
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'expectedCommercialOperationDate',
    fieldId: 'expectedCommercialOperationDate', label: 'Expected COD', format: 'date',
    extract: p => p.expectedCommercialOperationDate,
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'customerPricingStatus',
    fieldId: 'customerPricingStatus', label: 'Customer Pricing Status', format: 'text',
    extract: p => (p.customerPricingStatus ? (ProjectCustomerPricing as any)[p.customerPricingStatus] : undefined),
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'offtakeContractStatus',
    fieldId: 'offtakeContractStatus', label: 'Offtake Contract Status', format: 'text',
    extract: p => (p.offtakeContractStatus ? (ProjectOfftakeContract as any)[p.offtakeContractStatus] : undefined),
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'permitsStatus',
    fieldId: 'permitsStatus', label: 'Permits Status', format: 'text',
    extract: p => (p.permitsStatus ? (ProjectPermits as any)[p.permitsStatus] : undefined),
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'siteControlStatus',
    fieldId: 'siteControlStatus', label: 'Site Control Status', format: 'text',
    extract: p => (p.siteControlStatus ? (SiteControlStatusDisplay as any)[p.siteControlStatus] : undefined),
  },
  {
    sectionId: 'overview', subsectionId: 'keyDatesAndStatuses', id: 'interconnectionStatus',
    fieldId: 'interconnectionStatus', label: 'Interconnection Status', format: 'text',
    extract: p => (p.interconnectionStatus ? (ProjectInterconnectionDisplayValues as any)[p.interconnectionStatus] : undefined),
  },

  // System / PV
  {
    sectionId: 'system', subsectionId: 'pv', id: 'sizeKwdc',
    gateFieldId: 'projectArrays', label: 'System Size (kWdc)', format: 'integer', totalEligible: true,
    extract: p => arrayTotals(p).sizeKwdc,
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'sizeKwac',
    gateFieldId: 'projectArrays', label: 'System Size (kWac)', format: 'integer', totalEligible: true,
    extract: p => {
      const anyEntered = (p.projectArrays ?? []).some(a => typeof a.sizeKwac === 'number' && a.sizeKwac > 0);
      if (!anyEntered) return undefined;
      return arrayTotals(p).sizeKwac;
    },
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'productionFactor',
    gateFieldId: 'projectArrays', label: 'Seller\'s Estimated Yield (kWh/kW)', format: 'integer',
    extract: p => arrayTotals(p).productionFactor,
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'production',
    gateFieldId: 'projectArrays', label: 'Seller\'s Year 1 Production (kWh)', format: 'integer', totalEligible: true,
    extract: p => arrayTotals(p).production,
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'mountingType',
    gateFieldId: 'projectArrays', label: 'Mounting Type', format: 'text',
    extract: p => {
      const types = Array.from(new Set((p.projectArrays ?? []).map(a => a.type).filter(Boolean)));
      if (types.length === 0) return undefined;
      return types.map(t => (ProjectArraysDisplay as any)[t] ?? t).join(', ');
    },
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'roofCondition',
    gateFieldId: 'projectArrays', label: 'Roof Condition', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => {
      const conditions = (p.projectArrays ?? [])
        .filter(a => a.type === ProjectArrays.ROOFTOP)
        .map(a => a.roofCondition)
        .filter((c): c is string => typeof c === 'string' && c.trim().length > 0);
      if (conditions.length === 0) return undefined;
      return Array.from(new Set(conditions)).join('; ');
    },
  },
  {
    sectionId: 'system', subsectionId: 'pv', id: 'dnvEnergyYieldEst',
    label: 'DNV\'s Estimated Yield (kWh/kW)', format: 'integer',
    extract: p => p.dnvEnergyYieldEst,
  },

  // System / Storage
  {
    sectionId: 'system', subsectionId: 'storage', id: 'storageMake',
    gateFieldId: 'storage', label: 'Battery Make', format: 'text',
    extract: p => p.hasStorage ? p.storage?.[0]?.make : undefined,
  },
  {
    sectionId: 'system', subsectionId: 'storage', id: 'storageModel',
    gateFieldId: 'storage', label: 'Battery Model', format: 'text',
    extract: p => p.hasStorage ? p.storage?.[0]?.model : undefined,
  },
  {
    sectionId: 'system', subsectionId: 'storage', id: 'storageWattage',
    gateFieldId: 'storage', label: 'Battery Wattage (kW)', format: 'integer', totalEligible: true,
    extract: p => p.hasStorage ? p.storage?.[0]?.wattage : undefined,
  },
  {
    sectionId: 'system', subsectionId: 'storage', id: 'storageDischarge',
    gateFieldId: 'storage', label: 'Battery Discharge (kWh)', format: 'integer', totalEligible: true,
    extract: p => p.hasStorage ? p.storage?.[0]?.discharge : undefined,
  },

  // Budget / Upfront incentives — Expected ITC %, dynamic ITC Adder rows are
  // appended in collectSections, then safe harbor + upfront incentive.
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'expectedItc',
    gateFieldId: 'itcAdders', label: 'Expected Total ITC %', format: 'percentInteger',
    extract: p => getExpectedItc(p),
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'itcSafeHarbored',
    gateFieldId: 'itcAdders', label: 'ITC Safe Harbored?', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => {
      const v = p.itcDetails?.safeHarboredForItc;
      if (v === true) return 'Yes';
      if (v === false) return 'No';
      return undefined;
    },
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'itcSafeHarborPathway',
    gateFieldId: 'itcAdders', label: 'ITC Safe Harbor Pathway', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => formatStrategyList(p.itcDetails?.itcSafeHarborStrategy, ITC_SAFE_HARBOR_STRATEGY_LABELS),
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'itcSafeHarborDetails',
    gateFieldId: 'itcAdders', label: 'ITC Safe Harbor Details', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => (p.itcDetails as any)?.itcSafeHarborDetails,
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'feocSafeHarbored',
    gateFieldId: 'itcAdders', label: 'FEOC Safe Harbor Status', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => {
      const v = p.itcDetails?.safeHarboredForFeoc;
      if (v === true) return 'Yes';
      if (v === false) return 'No';
      return undefined;
    },
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'feocSafeHarborPathway',
    gateFieldId: 'itcAdders', label: 'FEOC Safe Harbor Pathway', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => formatStrategyList(p.itcDetails?.feocSafeHarborStrategy, FEOC_SAFE_HARBOR_STRATEGY_LABELS),
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'feocSafeHarborDetails',
    gateFieldId: 'itcAdders', label: 'FEOC Safe Harbor Details', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => (p.itcDetails as any)?.feocSafeHarborDetails,
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'upfrontIncentives',
    fieldId: 'upfrontIncentives', label: 'Upfront Incentives ($)', format: 'currency',
    // Show the user's original value and unit — do not convert $/Wdc to a total.
    labelFor: projects => `Upfront Incentives ${upfrontIncentiveUnitSuffix(projects)}`,
    formatFor: projects => upfrontIncentiveUnitSuffix(projects) === '($/Wdc)' ? 'currencyPerW' : 'currency',
    extract: upfrontIncentiveRaw,
  },
  {
    sectionId: 'budget', subsectionId: 'upfrontIncentives', id: 'upfrontIncentiveNotes',
    fieldId: 'upfrontIncentiveNotes', label: 'Upfront Incentive Notes', format: 'text',
    extract: p => p.upfrontIncentiveNotes,
  },

  // Budget / Budget items — $/Wdc stack
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'epcCostPerWdc',
    fieldId: 'epcCost', label: 'EPC Cost ($/Wdc)', format: 'currencyPerW',
    labelFor: projects => epcBudgetLabelFor(projects, '($/Wdc)'),
    bidPlaceholder: p => p.pricingType === PricingType.ACQUISITION || p.pricingType === PricingType.DEV_FEE,
    extract: epcCostPerWdc,
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetInterconnectionPerWdc',
    gateFieldId: 'budgetCosts', label: 'Interconnection Upgrades ($/Wdc)', format: 'currencyPerW',
    extract: (p, ctx) => budgetCostPerWdc(p, ctx, 'INTERCONNECTION'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetOtherItcPerWdc',
    gateFieldId: 'budgetCosts', label: 'Other ITC-Eligible Costs ($/Wdc)', format: 'currencyPerW',
    labelFor: projects => projects.length > 0 && projects.every(p => p.pricingType === PricingType.DEV_FEE)
      ? 'Estimate EPC Costs ($/Wdc)'
      : 'Other ITC-Eligible Costs ($/Wdc)',
    extract: (p, ctx) => budgetCostPerWdc(p, ctx, 'OTHER_ITC'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetOtherNonItcPerWdc',
    gateFieldId: 'budgetCosts', label: 'Other Non-ITC-Eligible Costs ($/Wdc)', format: 'currencyPerW',
    extract: (p, ctx) => budgetCostPerWdc(p, ctx, 'OTHER_NON_ITC'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'conductorFeePerWdc',
    gateFieldId: 'budgetCosts', label: 'Conductor Solar Fee ($/Wdc) - Paid by Buyer', format: 'currencyPerW',
    extract: conductorFeePerWdc,
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetTotalPerWdc',
    gateFieldId: 'budgetCosts', label: 'Total ($/Wdc)', format: 'currencyPerW',
    extract: budgetGrandTotalPerWdc,
  },

  // Budget / Budget items — $ stack
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'epcCost',
    fieldId: 'epcCost', label: 'EPC Cost ($)', format: 'currency', totalEligible: true,
    labelFor: projects => epcBudgetLabelFor(projects, '($)'),
    bidPlaceholder: p => p.pricingType === PricingType.ACQUISITION || p.pricingType === PricingType.DEV_FEE,
    extract: epcCostTotal,
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetInterconnection',
    gateFieldId: 'budgetCosts', label: 'Interconnection Upgrades ($)', format: 'currency', totalEligible: true,
    extract: (p, ctx) => budgetCostTotal(p, ctx, 'INTERCONNECTION'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetOtherItc',
    gateFieldId: 'budgetCosts', label: 'Other ITC-Eligible Costs ($)', format: 'currency', totalEligible: true,
    labelFor: projects => projects.length > 0 && projects.every(p => p.pricingType === PricingType.DEV_FEE)
      ? 'Estimate EPC Costs ($)'
      : 'Other ITC-Eligible Costs ($)',
    extract: (p, ctx) => budgetCostTotal(p, ctx, 'OTHER_ITC'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetOtherNonItc',
    gateFieldId: 'budgetCosts', label: 'Other Non-ITC-Eligible Costs ($)', format: 'currency', totalEligible: true,
    extract: (p, ctx) => budgetCostTotal(p, ctx, 'OTHER_NON_ITC'),
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'conductorFee',
    gateFieldId: 'budgetCosts', label: 'Conductor Solar Fee ($) - Paid by Buyer', format: 'currency', totalEligible: true,
    extract: conductorFeeTotal,
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetTotal',
    gateFieldId: 'budgetCosts', label: 'Total ($)', format: 'currency', totalEligible: true,
    extract: budgetGrandTotal,
  },
  {
    sectionId: 'budget', subsectionId: 'budgetItems', id: 'budgetNotes',
    gateFieldId: 'budgetCosts', label: 'Budget Notes', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => (p.budgetCosts?.OTHER_NON_ITC as any)?.notes,
  },

  // Revenue / Customer information
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'customerName',
    fieldId: 'customerName', label: 'Customer / Offtaker Name', format: 'text',
    extract: p => p.customerName,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'customerType',
    fieldId: 'customerType', label: 'Customer / Offtaker Type', format: 'text',
    extract: p => p.customerType,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'avoidedCostOfSolar',
    fieldId: 'avoidedCostOfSolar', label: 'Avoided Cost of Solar ($/kWh)', format: 'decimal4',
    extract: p => p.avoidedCostOfSolar,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'yearOneSavings',
    fieldId: 'yearOneSavings', label: 'Year 1 Savings ($)', format: 'currency', totalEligible: true,
    extract: p => p.yearOneSavings,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'customerCreditRating',
    fieldId: 'customerCreditRating', label: 'Customer / Offtaker Credit Rating', format: 'text',
    extract: p => p.customerCreditRating,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'customerCreditComments',
    fieldId: 'customerCreditComments', label: 'Customer / Offtaker Credit Comments', format: 'text',
    extract: p => p.customerCreditComments,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerInformation', id: 'communitySolarSubscriptionDetails',
    fieldId: 'communitySolarSubscriptionDetails', label: 'Community Solar Subscription Details', format: 'text',
    extract: p => p.communitySolarRevenueDetails ?? (p as any).communitySolarSubscriptionDetails,
  },

  // Revenue / Customer contract
  {
    sectionId: 'revenue', subsectionId: 'customerContract', id: 'contractType',
    fieldId: 'contractType', label: 'Contract Type', format: 'text',
    extract: p => (p.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS
      ? 'Community solar'
      : (p.contractType ? ContractTypeDisplay[p.contractType] : undefined)),
  },
  {
    sectionId: 'revenue', subsectionId: 'customerContract', id: 'contractTerm',
    fieldId: 'contractTerm', label: 'Contract Term (years)', unitSuffix: ' (years)', format: 'integer',
    extract: p => p.contractTerm,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerContract', id: 'contractRate',
    fieldId: 'contractRate', label: 'Contract Rate ($/kWh)', unitSuffix: ' ($/kWh)', format: 'decimal4',
    // Lease/ESA offtakes are quoted as an annual $ amount (no decimals); all
    // other contract types keep the worksheet-mirrored $/kWh label + format.
    labelFor: projects => isLeaseOrEsaRate(projects) ? 'Contract Rate ($/yr)' : undefined,
    formatFor: projects => isLeaseOrEsaRate(projects) ? 'currency' : undefined,
    bidPlaceholder: p => p.pricingType === PricingType.OFFTAKE,
    extract: p => p.contractRate,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerContract', id: 'communitySolarDiscountPercentage',
    fieldId: 'communitySolarDiscountPercentage', label: 'Community Solar Discount %', format: 'percent',
    extract: p => p.communitySolarDiscountPercentage,
  },
  {
    sectionId: 'revenue', subsectionId: 'customerContract', id: 'contractEscalator',
    fieldId: 'contractEscalator', label: 'Contract Escalator', format: 'percent',
    extract: p => p.contractEscalator,
  },

  // Revenue / RECs
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recOwnership',
    fieldId: 'recOwnership', label: 'REC Ownership', format: 'text',
    extract: p => (p.recOwnership === RecOwnership.INVESTOR
      ? RecOwnershipDisplay[RecOwnership.INVESTOR]
      : (p.recOwnership ? RecOwnershipDisplay[p.recOwnership] : undefined)),
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recDetails',
    fieldId: 'recDetails', label: 'REC Details', format: 'text',
    extract: p => p.recDetails,
  },
  // State-specific REC details (IL SHINES + NJ). Mirrors the legacy downloadExcel
  // sheet: only shown when the investor owns the RECs and the state has data.
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recIlShines',
    label: 'IL SHINES Eligible?', format: 'text', hideWhenAllEmpty: true,
    extract: p => {
      if (p.recOwnership !== RecOwnership.INVESTOR || p.addressState !== RecStates.ILLINOIS) return undefined;
      return recsDataFor(p)?.isShinesProgram === RecShinesProgram.YES ? 'Yes' : 'No';
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recAllocation',
    label: 'RECs secured?', format: 'text', hideWhenAllEmpty: true,
    extract: p => {
      if (!isIllinoisShines(p) && !isNewJersey(p)) return undefined;
      const allocation = recsDataFor(p)?.recAllocation;
      return allocation ? (RecAllocationDisplay as any)[allocation] : undefined;
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recProjectCategory',
    label: 'Project category', format: 'text', hideWhenAllEmpty: true,
    extract: p => {
      if (!isIllinoisShines(p)) return undefined;
      const category = recsDataFor(p)?.recProjectCategory;
      return category ? (RecProjectCategoryDisplay as any)[category] : undefined;
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recUtilityGroup',
    label: 'Utility Group', format: 'text', hideWhenAllEmpty: true,
    extract: p => {
      if (!isIllinoisShines(p)) return undefined;
      const group = recsDataFor(p)?.recUtilityGroup;
      return group ? (RecUtilityGroupExcelDisplay as any)[group] : undefined;
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recValue',
    label: 'REC Value ($/MWh)', format: 'decimal2', hideWhenAllEmpty: true,
    extract: p => {
      if (!isIllinoisShines(p) && !isNewJersey(p)) return undefined;
      return recsDataFor(p)?.recValue ?? undefined;
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recProgramYear',
    label: 'Program year', format: 'text', hideWhenAllEmpty: true,
    extract: p => {
      if (!isIllinoisShines(p)) return undefined;
      const year = recsDataFor(p)?.recProgramYear;
      return year ? (RecProgramYearDisplay as any)[year] : undefined;
    },
  },
  {
    sectionId: 'revenue', subsectionId: 'renewableEnergyCredits', id: 'recContractTerm',
    label: 'REC Term (years)', format: 'integer', hideWhenAllEmpty: true,
    extract: p => {
      if (!isNewJersey(p)) return undefined;
      return recsDataFor(p)?.recContractTerm ?? undefined;
    },
  },

  // Expenses / Site expenses
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'siteLeasePerYear',
    fieldId: 'siteLeaseCost', label: 'Site Lease ($/year)', unitSuffix: ' ($/year)', format: 'currency', totalEligible: true,
    extract: siteLeasePerYear,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'siteLeaseEscalator',
    fieldId: 'siteLeaseEscalator', label: 'Site Lease Escalator', format: 'percent',
    extract: p => p.siteLeaseEscalator,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'siteLeaseTerm',
    fieldId: 'siteLeaseTerm', label: 'Site Lease Term', format: 'text',
    extract: p => p.siteLeaseTerm ? (SiteLeaseTermDisplayExcel as any)[p.siteLeaseTerm] : undefined,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'siteLeaseExtensions',
    fieldId: 'siteLeaseExtensions', label: 'Site Lease Renewals (years)', format: 'integer',
    extract: p => p.siteLeaseExtensions,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'unusualSiteConditions',
    fieldId: 'unusualSiteConditions', label: 'Unusual Site Conditions', format: 'text',
    hideWhenAllEmpty: true,
    extract: p => p.unusualSiteConditions,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'propertyTaxType',
    fieldId: 'propertyTaxType', label: 'Property Tax Type', format: 'text',
    extract: p => p.propertyTaxType ? (PropertyTaxTypeDisplayValues as any)[p.propertyTaxType] : undefined,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'propertyTaxYr1',
    fieldId: 'propertyTaxAmount', label: 'Property Taxes — Yr 1 ($)', unitSuffix: ' — Yr 1 ($)', format: 'currency', totalEligible: true,
    extract: propertyTaxPerYear,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'propertyTaxEscalator',
    fieldId: 'propertyTaxEscalator', label: 'Property Tax Escalator', format: 'percent',
    extract: p => p.propertyTaxEscalator,
  },
  {
    sectionId: 'expenses', subsectionId: 'siteExpenses', id: 'propertyTaxNotes',
    fieldId: 'propertyTaxNotes', label: 'Property Tax Notes', format: 'text',
    extract: p => p.propertyTaxNotes,
  },

  // Expenses / O&M
  {
    sectionId: 'expenses', subsectionId: 'operationAndMaintenance', id: 'omProposeFlag',
    fieldId: 'omProposeFlag', label: 'EPC Proposing O&M?', format: 'yesNo',
    extract: p => p.omProposeFlag,
  },
  {
    sectionId: 'expenses', subsectionId: 'operationAndMaintenance', id: 'omPrice',
    fieldId: 'omPrice', label: 'O&M Price ($/year)', unitSuffix: ' ($/year)', format: 'currency', totalEligible: true,
    extract: omPerYear,
  },
  {
    sectionId: 'expenses', subsectionId: 'operationAndMaintenance', id: 'omEscalator',
    fieldId: 'omEscalator', label: 'O&M Escalator', format: 'percent',
    extract: p => p.omEscalator,
  },
];

/** Subset of additional revenue/expense rows are emitted by a separate dynamic-expander phase in the generator. */
export const dynamicSections = {
  additionalRevenue: {
    sectionId: 'revenue',
    subsectionId: 'otherRevenueStreams',
    fieldId: 'additionalRevenues',
    headerLabel: 'Additional Revenue',
  },
  additionalOperatingExpenses: {
    sectionId: 'expenses',
    subsectionId: 'operatingExpenses',
    fieldId: 'additionalOperatingExpenses',
    headerLabel: 'Additional Operating Expense',
  },
} as const;

export const additionalRevenuePerYear = (item: { amount?: number; unit?: string }, project: Project): number | undefined => {
  const amount = Number(item.amount);
  if (!Number.isFinite(amount)) return undefined;
  const unit = typeof item.unit === 'string' ? item.unit.toUpperCase() : item.unit;
  if (unit === RevenueUnit.YEAR) return amount;
  const totals = arrayTotals(project);
  if (unit === RevenueUnit.KWH) {
    const production = Number(totals.production);
    return Number.isFinite(production) && production > 0 ? amount * production : undefined;
  }
  if (unit === 'KWDC') {
    const sizeKwdc = Number(totals.sizeKwdc);
    return Number.isFinite(sizeKwdc) && sizeKwdc > 0 ? amount * sizeKwdc : undefined;
  }
  if (unit === 'KWAC') {
    const sizeKwac = Number(totals.sizeKwac);
    return Number.isFinite(sizeKwac) && sizeKwac > 0 ? amount * sizeKwac : undefined;
  }
  return undefined;
};

export const additionalExpensePerYear = (item: { amount?: number; unit?: string }, project: Project): number | undefined => {
  const amount = Number(item.amount);
  if (!Number.isFinite(amount)) return undefined;
  const unit = typeof item.unit === 'string' ? item.unit.toUpperCase() : item.unit;
  if (unit === ExpensesUnit.YEAR) return amount;
  const totals = arrayTotals(project);
  if (unit === ExpensesUnit.KWDC || unit === 'WDC') {
    const sizeKwdc = Number(totals.sizeKwdc);
    return Number.isFinite(sizeKwdc) && sizeKwdc > 0 ? amount * sizeKwdc : undefined;
  }
  if (unit === 'KWAC') {
    const sizeKwac = Number(totals.sizeKwac);
    return Number.isFinite(sizeKwac) && sizeKwac > 0 ? amount * sizeKwac : undefined;
  }
  if (unit === 'KWH') {
    const production = Number(totals.production);
    return Number.isFinite(production) && production > 0 ? amount * production : undefined;
  }
  return undefined;
};
